import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Loader2, CheckCircle2, AlertCircle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const STATUS_LABELS = {
  sent: { label: 'Envoyé', color: '#7C9C82', icon: CheckCircle2 },
  not_configured: { label: 'Non configuré', color: '#AE9885', icon: AlertCircle },
  error: { label: 'Erreur', color: '#C76542', icon: AlertCircle },
};

export default function EmailLogPanel() {
  const [data, setData] = useState({ items: [], total: 0, by_status_30d: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = async (query = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/emails/log?limit=200&q=${encodeURIComponent(query)}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error("Impossible de charger le journal des emails.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  if (loading) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  const sent = data.by_status_30d?.sent || 0;
  const errored = (data.by_status_30d?.error || 0)
    + Object.keys(data.by_status_30d || {}).filter((k) => k.startsWith('http_'))
        .reduce((sum, k) => sum + (data.by_status_30d[k] || 0), 0);

  return (
    <Card className="border border-[#8C7261]/30 rounded-2xl bg-white" data-testid="email-log-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-[#8C7261]" strokeWidth={1.8} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8C7261] font-semibold">Journal Emails</span>
            </div>
            <h3 className="font-heading text-lg text-[#493329]">Tous les emails envoyés</h3>
            <p className="text-xs text-[#7A6455] mt-1">Visibilité complète sur ce qui part — destinataires, sujets, statuts.</p>
          </div>
          <Button onClick={() => load(q)} variant="outline" size="sm" className="rounded-full text-xs h-8 border-[#E9DDCC] text-[#7A6455]">Actualiser</Button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#F5EFE5] rounded-xl p-3 text-center border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Total (toutes périodes)</div>
            <div className="font-heading text-2xl text-[#493329]">{data.total}</div>
          </div>
          <div className="bg-[#F1F6F0] rounded-xl p-3 text-center border border-[#7C9C82]/30">
            <div className="text-[10px] uppercase text-[#7C9C82] tracking-wider mb-1">Envoyés (30j)</div>
            <div className="font-heading text-2xl text-[#7C9C82]">{sent}</div>
          </div>
          <div className="bg-[#FFF4F0] rounded-xl p-3 text-center border border-[#C76542]/30">
            <div className="text-[10px] uppercase text-[#C76542] tracking-wider mb-1">Échecs (30j)</div>
            <div className="font-heading text-2xl text-[#C76542]">{errored}</div>
          </div>
        </div>

        <form onSubmit={onSearch} className="flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par email ou sujet…"
            className="h-10 rounded-xl border-[#E9DDCC] bg-[#F5EFE5] text-sm"
            data-testid="email-log-search"
          />
          <Button type="submit" className="h-10 px-4 rounded-xl bg-[#493329] hover:bg-[#C99A55] text-white">
            <Search className="w-4 h-4" />
          </Button>
        </form>

        <div className="bg-white rounded-xl border border-[#E9DDCC] max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5EFE5] sticky top-0">
              <tr className="text-[10px] uppercase tracking-wider text-[#AE9885]">
                <th className="px-3 py-2 text-left font-semibold">Date</th>
                <th className="px-3 py-2 text-left font-semibold">Destinataire</th>
                <th className="px-3 py-2 text-left font-semibold">Sujet</th>
                <th className="px-3 py-2 text-center font-semibold">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EFE4D0]">
              {data.items.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-[#AE9885] italic py-6">Aucun email enregistré pour le moment.</td></tr>
              ) : data.items.map((e, i) => {
                const st = STATUS_LABELS[e.status] || { label: e.status, color: '#AE9885', icon: AlertCircle };
                const Icon = st.icon;
                return (
                  <tr key={i}>
                    <td className="px-3 py-2 text-[10px] text-[#7A6455] whitespace-nowrap">
                      {e.ts ? new Date(e.ts).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td className="px-3 py-2 text-[#493329] truncate max-w-[160px]">{e.to}</td>
                    <td className="px-3 py-2 text-[#493329] truncate max-w-[260px]">{e.subject}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium" style={{ color: st.color }}>
                        <Icon className="w-3 h-3" /> {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-[#AE9885] italic">
          Affiche les 200 derniers envois. Tous les emails (bienvenue, relance, contact, reset, notifications coach) sont tracés.
        </p>
      </CardContent>
    </Card>
  );
}
